import api from './api';
import { formatMoney } from '../utils/currency';
import {
  PARTNER_PLAN_BILLING_INTERVALS,
  PARTNER_PLAN_CURRENCY_OPTIONS,
  PARTNER_PLAN_GRACE_PERIOD_MAX,
  PARTNER_PLAN_GRACE_PERIOD_MIN,
  PARTNER_PLAN_MAX_RIDES_MAX,
  PARTNER_PLAN_MAX_RIDES_MIN,
  PARTNER_PLAN_PRICE_MAX,
  PARTNER_PLAN_PRICE_MIN,
  PARTNER_PLAN_SELECTABLE_TYPES,
  PARTNER_PLAN_TRIAL_DAYS_MAX,
  PARTNER_PLAN_TRIAL_DAYS_MIN,
  PARTNER_PLAN_VALIDITY_DAYS_MAX,
  PARTNER_PLAN_VALIDITY_DAYS_MIN,
  VALIDITY_BY_INTERVAL,
  type PartnerPlanBillingInterval,
  type PartnerPlanFormTabId,
  type PartnerPlanType,
} from '../constants/partnerSubscriptionPlanConfig';
import { validatePersonName, sanitizePersonName } from '../utils/personFieldValidation';

export interface PartnerSubscriptionPlanRow {
  _id: string;
  code: string;
  name: string;
  description?: string;
  price: number;
  currency: string;
  billingInterval: PartnerPlanBillingInterval | string;
  validityDays: number;
  zoneId: string | { _id: string; name?: string; code?: string; city?: string };
  zoneCode: string;
  zoneIds?: Array<string | { _id: string; name?: string; code?: string; city?: string }>;
  zoneCodes?: string[];
  partnerType: PartnerPlanType | string;
  partnerTypes?: string[];
  features: string[];
  maxRidesPerPeriod?: number;
  commissionWaived: boolean;
  autoRenew: boolean;
  gracePeriodDays: number;
  trialDays: number;
  badgeLabel?: string;
  isFeatured: boolean;
  isActive: boolean;
  isPublic: boolean;
  sortOrder: number;
  activeSubscriberCount?: number;
}

export interface PartnerSubscriptionPlanPayload {
  code: string;
  name: string;
  description?: string;
  price: number;
  currency: string;
  billingInterval: PartnerPlanBillingInterval | string;
  validityDays: number;
  zoneIds: string[];
  partnerType: PartnerPlanType | string;
  partnerTypes?: string[];
  features: string[];
  maxRidesPerPeriod?: number;
  commissionWaived: boolean;
  autoRenew: boolean;
  gracePeriodDays: number;
  trialDays: number;
  badgeLabel?: string;
  isFeatured: boolean;
  isActive: boolean;
  isPublic: boolean;
  sortOrder: number;
}

export const BILLING_INTERVAL_OPTIONS = PARTNER_PLAN_BILLING_INTERVALS;
export const PLAN_CURRENCY_OPTIONS = PARTNER_PLAN_CURRENCY_OPTIONS;

export function extractApiError(err: unknown): string {
  const e = err as { response?: { data?: { message?: string } } };
  return e.response?.data?.message || 'Something went wrong';
}

export function zoneIdFromRef(ref: PartnerSubscriptionPlanRow['zoneId']): string {
  if (typeof ref === 'object' && ref?._id) return ref._id;
  return String(ref || '');
}

export function zoneIdsFromRow(row: PartnerSubscriptionPlanRow): string[] {
  if (row.zoneIds?.length) {
    return row.zoneIds.map((z) => (typeof z === 'object' && z?._id ? z._id : String(z))).filter(Boolean);
  }
  const single = zoneIdFromRef(row.zoneId);
  return single ? [single] : [];
}

export function zoneLabels(row: PartnerSubscriptionPlanRow): string[] {
  if (row.zoneIds?.length) {
    return row.zoneIds.map((z, i) => {
      if (typeof z === 'object' && z) {
        const name = z.name?.trim() || '';
        const code = z.code?.trim() || row.zoneCodes?.[i]?.trim() || '';
        if (name && code && name.toUpperCase() !== code.toUpperCase()) return `${name} (${code})`;
        return name || code;
      }
      return row.zoneCodes?.[i] || String(z);
    }).filter(Boolean);
  }
  if (row.zoneCodes?.length) return row.zoneCodes.filter(Boolean);
  const label = zoneLabel(row);
  return label ? [label] : [];
}

export function zoneLabel(row: PartnerSubscriptionPlanRow): string {
  const labels = zoneLabels(row);
  if (labels.length > 1) return `${labels.length} zones`;
  return labels[0] || row.zoneCode || '';
}

/** Unique plan code from display name (lowercase, a-z 0-9 _ -). */
export function slugifyPartnerPlanCode(value: string) {
  return value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .replace(/_+/g, '_')
    .slice(0, 48);
}

export function partnerPlanBillingLabel(interval: string): string {
  return PARTNER_PLAN_BILLING_INTERVALS.find((o) => o.value === interval)?.label
    || interval.charAt(0).toUpperCase() + interval.slice(1);
}

function partnerPlanBillingPerUnit(interval: string): string {
  const units: Record<string, string> = {
    daily: 'day',
    weekly: 'week',
    monthly: 'month',
    yearly: 'year',
  };
  return units[interval] || interval;
}

export interface PartnerPlanPriceDisplay {
  amount: string;
  cadence: string;
  validity: string;
  summary: string;
}

export function getPartnerPlanPriceDisplay(
  row: Pick<PartnerSubscriptionPlanRow, 'price' | 'currency' | 'billingInterval' | 'validityDays'>,
): PartnerPlanPriceDisplay {
  const currency = row.currency || undefined;
  const interval = String(row.billingInterval || 'weekly');
  const validityDays = Number(row.validityDays) || 0;

  if (!row.price) {
    return {
      amount: 'Free',
      cadence: '',
      validity: validityDays ? `${validityDays}-day validity` : '',
      summary: validityDays ? `Free · ${validityDays}-day validity` : 'Free',
    };
  }

  const amount = formatMoney(row.price, currency);
  const cadence = `per ${partnerPlanBillingPerUnit(interval)}`;
  const validity = validityDays ? `${validityDays}-day validity` : '';
  const summary = validity ? `${amount} ${cadence} · ${validity}` : `${amount} ${cadence}`;

  return { amount, cadence, validity, summary };
}

export function findRepeatedWord(text: string): string | null {
  const words = text
    .trim()
    .split(/\s+/)
    .map((w) => w.replace(/^[^a-zA-Z0-9]+|[^a-zA-Z0-9]+$/g, ''))
    .filter(Boolean);
  for (let i = 1; i < words.length; i += 1) {
    if (words[i].toLowerCase() === words[i - 1].toLowerCase()) {
      return words[i];
    }
  }
  return null;
}

export function validatePartnerPlanDescription(description?: string): string | undefined {
  if (!description?.trim()) return undefined;
  const repeated = findRepeatedWord(description);
  if (repeated) return `Description cannot contain repeated words ("${repeated}")`;
  return undefined;
}

export function partnerTypesFromRow(row: { partnerType?: string; partnerTypes?: string[] }): string[] {
  const selectable = PARTNER_PLAN_SELECTABLE_TYPES.map((o) => o.value);
  if (row.partnerTypes?.length) {
    return row.partnerTypes.filter((t) => selectable.includes(t as typeof selectable[number]));
  }
  if (row.partnerType && row.partnerType !== 'all') {
    return selectable.includes(row.partnerType as typeof selectable[number]) ? [row.partnerType] : ['driver'];
  }
  if (row.partnerType === 'all') return [...selectable];
  return ['driver'];
}

export function resolvePartnerTypeFields(partnerTypes: string[]): { partnerType: string; partnerTypes: string[] } {
  const selectable = PARTNER_PLAN_SELECTABLE_TYPES.map((o) => o.value);
  const unique = [...new Set(partnerTypes.filter((t) => selectable.includes(t as typeof selectable[number])))];
  if (!unique.length) return { partnerType: 'driver', partnerTypes: ['driver'] };
  if (unique.length === 1) return { partnerType: unique[0], partnerTypes: unique };
  if (unique.length >= selectable.length) return { partnerType: 'all', partnerTypes: selectable };
  return { partnerType: 'all', partnerTypes: unique };
}

export function formatPartnerPlanTypesLabel(row: { partnerType?: string; partnerTypes?: string[] }): string {
  const types = partnerTypesFromRow(row);
  if (types.length >= PARTNER_PLAN_SELECTABLE_TYPES.length) return 'All partner types';
  return types
    .map((t) => PARTNER_PLAN_SELECTABLE_TYPES.find((o) => o.value === t)?.label || t)
    .join(', ');
}

/** Single-line price label for exports and sort values. */
export function formatPartnerPlanPrice(
  row: Pick<PartnerSubscriptionPlanRow, 'price' | 'currency' | 'billingInterval' | 'validityDays'>,
) {
  return getPartnerPlanPriceDisplay(row).summary;
}

export async function listPartnerSubscriptionPlans(params?: {
  zoneId?: string;
  active?: boolean;
  public?: boolean;
  partnerType?: string;
}) {
  const res = await api.get('/admin/partner-subscription-plans', { params });
  const rows = res.data.data as unknown[];
  return rows.map((row) => normalizePlanRow(row as Record<string, unknown>));
}

export async function createPartnerSubscriptionPlan(payload: PartnerSubscriptionPlanPayload) {
  const res = await api.post('/admin/partner-subscription-plans', payload);
  return normalizePlanRow(res.data.data as Record<string, unknown>);
}

export async function updatePartnerSubscriptionPlan(id: string, payload: Partial<PartnerSubscriptionPlanPayload>) {
  const res = await api.put(`/admin/partner-subscription-plans/${id}`, payload);
  return normalizePlanRow(res.data.data as Record<string, unknown>);
}

export async function setPartnerSubscriptionPlanStatus(id: string, isActive: boolean) {
  const res = await api.patch(`/admin/partner-subscription-plans/${id}/status`, { isActive });
  return normalizePlanRow(res.data.data as Record<string, unknown>);
}

export async function deletePartnerSubscriptionPlan(id: string) {
  const res = await api.delete(`/admin/partner-subscription-plans/${id}`);
  return res.data;
}

function normalizePlanRow(row: Record<string, unknown>): PartnerSubscriptionPlanRow {
  return {
    _id: String(row._id),
    code: String(row.code || ''),
    name: String(row.name || ''),
    description: row.description ? String(row.description) : '',
    price: Number(row.price) || 0,
    currency: String(row.currency || 'INR'),
    billingInterval: String(row.billingInterval || 'weekly'),
    validityDays: Number(row.validityDays) || 7,
    zoneId: (row.zoneId as PartnerSubscriptionPlanRow['zoneId']) || String(row.zoneId || ''),
    zoneCode: String(row.zoneCode || ''),
    zoneIds: Array.isArray(row.zoneIds)
      ? (row.zoneIds as PartnerSubscriptionPlanRow['zoneIds'])
      : row.zoneId
        ? [row.zoneId as PartnerSubscriptionPlanRow['zoneId']]
        : [],
    zoneCodes: Array.isArray(row.zoneCodes)
      ? row.zoneCodes.map(String)
      : row.zoneCode
        ? [String(row.zoneCode)]
        : [],
    partnerType: String(row.partnerType || 'driver'),
    partnerTypes: Array.isArray(row.partnerTypes)
      ? row.partnerTypes.map(String)
      : partnerTypesFromRow({ partnerType: String(row.partnerType || 'driver') }),
    features: Array.isArray(row.features) ? row.features.map(String) : [],
    maxRidesPerPeriod: row.maxRidesPerPeriod != null ? Number(row.maxRidesPerPeriod) : undefined,
    commissionWaived: row.commissionWaived !== false,
    autoRenew: row.autoRenew !== false,
    gracePeriodDays: Number(row.gracePeriodDays) || 0,
    trialDays: Number(row.trialDays) || 0,
    badgeLabel: row.badgeLabel ? String(row.badgeLabel) : undefined,
    isFeatured: !!row.isFeatured,
    isActive: row.isActive !== false,
    isPublic: row.isPublic !== false,
    sortOrder: Number(row.sortOrder) || 0,
    activeSubscriberCount: Number(row.activeSubscriberCount) || 0,
  };
}

export interface PartnerPlanUsage {
  activeSubscriberCount: number;
  canDelete: boolean;
  canDeactivate: boolean;
  samplePartners: Array<{ _id: string; name: string; userType?: string; isActive?: boolean }>;
}

export async function getPartnerPlanUsage(planId: string) {
  const res = await api.get(`/admin/partner-subscription-plans/${planId}/usage`);
  return res.data.data as PartnerPlanUsage;
}

export function emptyPartnerPlanForm(overrides?: Partial<PartnerSubscriptionPlanPayload>): PartnerSubscriptionPlanPayload {
  return {
    code: '',
    name: '',
    description: '',
    price: 0,
    currency: 'INR',
    billingInterval: 'weekly',
    validityDays: 7,
    zoneIds: [],
    partnerType: 'driver',
    partnerTypes: ['driver'],
    maxRidesPerPeriod: undefined,
    commissionWaived: true,
    autoRenew: true,
    gracePeriodDays: 1,
    trialDays: 0,
    badgeLabel: '',
    isFeatured: false,
    isActive: true,
    isPublic: true,
    features: [],
    sortOrder: 0,
    ...overrides,
  };
}

export function planRowToForm(row: PartnerSubscriptionPlanRow): PartnerSubscriptionPlanPayload {
  return emptyPartnerPlanForm({
    code: row.code,
    name: row.name,
    description: row.description || '',
    price: row.price ?? 0,
    currency: row.currency || 'INR',
    billingInterval: row.billingInterval || 'weekly',
    validityDays: row.validityDays ?? 7,
    zoneIds: zoneIdsFromRow(row),
    ...resolvePartnerTypeFields(partnerTypesFromRow(row)),
    features: row.features || [],
    maxRidesPerPeriod: row.maxRidesPerPeriod,
    commissionWaived: row.commissionWaived !== false,
    autoRenew: row.autoRenew !== false,
    gracePeriodDays: row.gracePeriodDays ?? 0,
    trialDays: row.trialDays ?? 0,
    badgeLabel: row.badgeLabel || '',
    isFeatured: !!row.isFeatured,
    isActive: row.isActive !== false,
    isPublic: row.isPublic !== false,
    sortOrder: row.sortOrder ?? 0,
  });
}

export function validatePartnerPlanForm(form: PartnerSubscriptionPlanPayload, tabId?: PartnerPlanFormTabId): Record<string, string> {
  const errors: Record<string, string> = {};
  const checkInfo = !tabId || tabId === 'info';
  const checkPricing = !tabId || tabId === 'pricing';
  const checkBenefits = !tabId || tabId === 'benefits';

  if (checkInfo) {
    if (!form.code.trim()) errors.code = 'Plan code is required';
    else if (!/^[a-z0-9_-]+$/.test(form.code.trim())) errors.code = 'Use lowercase letters, numbers, hyphen, or underscore';
    const nameErr = validatePersonName(form.name, 'Plan name', { required: true, min: 2, max: 80 });
    if (nameErr) errors.name = nameErr;
    const descErr = validatePartnerPlanDescription(form.description);
    if (descErr) errors.description = descErr;
    if (!form.zoneIds.length) errors.zoneIds = 'Select at least one zone';
    if (!form.partnerTypes?.length) errors.partnerTypes = 'Select at least one partner type';
  }

  if (checkPricing) {
    if (form.price < PARTNER_PLAN_PRICE_MIN || form.price > PARTNER_PLAN_PRICE_MAX) {
      errors.price = `Price must be between ${PARTNER_PLAN_PRICE_MIN} and ${PARTNER_PLAN_PRICE_MAX.toLocaleString('en-IN')}`;
    }
    if (form.validityDays < PARTNER_PLAN_VALIDITY_DAYS_MIN || form.validityDays > PARTNER_PLAN_VALIDITY_DAYS_MAX) {
      errors.validityDays = `Validity must be between ${PARTNER_PLAN_VALIDITY_DAYS_MIN} and ${PARTNER_PLAN_VALIDITY_DAYS_MAX} days`;
    }
    if (form.trialDays < PARTNER_PLAN_TRIAL_DAYS_MIN || form.trialDays > PARTNER_PLAN_TRIAL_DAYS_MAX) {
      errors.trialDays = `Trial days must be between ${PARTNER_PLAN_TRIAL_DAYS_MIN} and ${PARTNER_PLAN_TRIAL_DAYS_MAX}`;
    }
    if (form.gracePeriodDays < PARTNER_PLAN_GRACE_PERIOD_MIN || form.gracePeriodDays > PARTNER_PLAN_GRACE_PERIOD_MAX) {
      errors.gracePeriodDays = `Grace period must be between ${PARTNER_PLAN_GRACE_PERIOD_MIN} and ${PARTNER_PLAN_GRACE_PERIOD_MAX} days`;
    }
    if (form.maxRidesPerPeriod != null) {
      if (form.maxRidesPerPeriod < PARTNER_PLAN_MAX_RIDES_MIN || form.maxRidesPerPeriod > PARTNER_PLAN_MAX_RIDES_MAX) {
        errors.maxRidesPerPeriod = `Max rides must be between ${PARTNER_PLAN_MAX_RIDES_MIN} and ${PARTNER_PLAN_MAX_RIDES_MAX.toLocaleString('en-IN')}, or leave blank for unlimited`;
      }
    }
  }

  if (checkBenefits && form.isFeatured && !form.badgeLabel?.trim()) {
    errors.badgeLabel = 'Badge label is required for featured plans';
  }

  return errors;
}

export function buildPartnerPlanPayload(form: PartnerSubscriptionPlanPayload): PartnerSubscriptionPlanPayload {
  const billingInterval = (form.billingInterval || 'weekly') as PartnerPlanBillingInterval;
  const typeFields = resolvePartnerTypeFields(form.partnerTypes || partnerTypesFromRow(form));
  return {
    code: form.code.trim().toLowerCase(),
    name: form.name.trim(),
    description: form.description?.trim() || undefined,
    price: Number(form.price) || 0,
    currency: form.currency || 'INR',
    billingInterval,
    validityDays: form.validityDays || VALIDITY_BY_INTERVAL[billingInterval] || 7,
    zoneIds: [...new Set(form.zoneIds.filter(Boolean))],
    partnerType: typeFields.partnerType,
    partnerTypes: typeFields.partnerTypes,
    features: form.features.filter(Boolean),
    maxRidesPerPeriod: form.maxRidesPerPeriod != null ? Number(form.maxRidesPerPeriod) : undefined,
    commissionWaived: form.commissionWaived,
    autoRenew: form.autoRenew,
    gracePeriodDays: Number(form.gracePeriodDays) || 0,
    trialDays: Number(form.trialDays) || 0,
    badgeLabel: form.badgeLabel?.trim() || undefined,
    isFeatured: form.isFeatured,
    isActive: form.isActive,
    isPublic: form.isPublic,
    sortOrder: Number(form.sortOrder) || 0,
  };
}
